import { MessageType, ToastMessages } from '@triumph/design-system';
import { Fragment, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router-dom';

import { useAppContext } from '../../../components/providers/AppContextProvider';
import { PageRoutes } from '../../../components/router/routes';
import { CompanyLogoStandard } from '../../../shared/components/CompanyLogo';
import { TriumphContactUrl } from '../../../shared/constants/companies/utils';
import { ExternalRoutes } from '../../../shared/routes';
import { LayoutFooterLink } from './components/LayoutFooterLink';
import {
  ContactUsContainer,
  ContentContainer,
  FooterContainer,
  FooterDivider,
  FooterItem,
  HeaderContainer,
  LogoContainer,
  MobileCopyrightContainer,
  PublicLayoutContainer,
} from './PublicLayout.StyledComponents';
import { PublicLayoutProps } from './PublicLayout.types';

const PublicLayout: React.FC<PublicLayoutProps> = ({ children }) => {
  // get context data
  const { breakpoint } = useAppContext();
  const showContactUs = true;
  // get translations
  const { t } = useTranslation('common');

  // set up component state
  const [toastMessage, setToastMessage] = useState('');
  const [showMessage, setShowMessage] = useState(false);

  // call hooks
  const location = useLocation();

  // define variables
  const messages = [
    {
      handleClose: () => reset(),
      type: MessageType.success,
      message: toastMessage,
      show: showMessage,
      messageId: 1,
      position: 'topCenter',
      toastTestId: 'account_created_toast',
    },
  ];

  // helpers
  const reset = () => {
    setShowMessage(false);
    setToastMessage('');
    sessionStorage.removeItem('toastMessage');
  };

  // effects
  useEffect(() => {
    const message = sessionStorage.getItem('toastMessage') || '';
    setToastMessage(message);
    setShowMessage(true);
  }, [location.pathname]);

  return (
    <>
      {toastMessage && <ToastMessages messages={messages} />}
      <PublicLayoutContainer
        breakpoint={breakpoint}
        data-testid="layout-container"
      >
        <ContentContainer breakpoint={breakpoint}>
          <HeaderContainer
            data-testid="headerContainer"
            breakpoint={breakpoint}
          >
            <LogoContainer data-testid="logoContainer" breakpoint={breakpoint}>
              <CompanyLogoStandard size={180} data-testid="logo" />
            </LogoContainer>

            {['sm', 'md', 'lg', 'xl', 'xxl'].includes(breakpoint) &&
              showContactUs && (
                <ContactUsContainer>
                  <LayoutFooterLink
                    href={TriumphContactUrl}
                    linkText="contactUs"
                  />
                </ContactUsContainer>
              )}
          </HeaderContainer>
          {children && <Fragment>{children}</Fragment>}
          <Outlet />
          {breakpoint === 'xs' && (
            <MobileCopyrightContainer>
              <FooterItem>
                &copy;
                {` ${new Date().getFullYear()} `}
                <Trans ns="common" i18nKey="copyright" shouldUnescape={true} />
              </FooterItem>
            </MobileCopyrightContainer>
          )}
          <FooterContainer breakpoint={breakpoint}>
            {breakpoint !== 'xs' && (
              <>
                <FooterItem>
                  &copy;
                  {` ${new Date().getFullYear()} `}
                  <Trans
                    ns="common"
                    i18nKey="copyright"
                    shouldUnescape={true}
                  />
                </FooterItem>
              </>
            )}
            {breakpoint === 'xs' && showContactUs && (
              <>
                <LayoutFooterLink
                  href={TriumphContactUrl}
                  linkText="contactUs"
                />
                <FooterDivider />
              </>
            )}
            <LayoutFooterLink
              href={ExternalRoutes.TbkPrivacyPolicy}
              linkText="privacyPolicy"
            />
            <FooterDivider />
            <LayoutFooterLink
              href={PageRoutes.TERMS}
              linkText="termsAndConditions"
            />
          </FooterContainer>
        </ContentContainer>
      </PublicLayoutContainer>
    </>
  );
};

export default PublicLayout;
